import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';
import SEOMeta from '../../components/SEOMeta';
import ThemeToggle from '../../components/ThemeToggle';
import { useDarkMode } from '../../hooks/useDarkMode';

export default function DataDeletionPage() {
  const isDark = useDarkMode();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <SEOMeta
        title="Exclusão de Dados | Inspira Músicas"
        description="Saiba como solicitar a exclusão dos seus dados pessoais da Inspira Músicas."
        path="/exclusao-de-dados"
        noIndex
      />
    <div style={{ background: 'var(--cream)', minHeight: '100vh' }}>
      {/* Header */}
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${scrolled ? 'shadow-sm' : ''}`}
        style={{
          background: scrolled ? 'var(--warm-white)' : 'transparent',
          borderBottom: scrolled ? '1px solid var(--beige-mid)' : 'none',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 cursor-pointer">
            <img
              src={isDark ? '/logo-dark.png' : '/logo-light.png'}
              alt="Inspira Músicas"
              className="h-9 w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            className="hidden md:flex items-center gap-2 text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
            style={{ color: 'var(--earth)', fontFamily: "'DM Sans', sans-serif" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--gold)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--earth)')}
          >
            <i className="ri-arrow-left-line text-base" />
            Voltar ao início
          </Link>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Link
              to="/#preco"
              className="px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
              style={{ background: 'var(--gold)', color: '#fff', fontFamily: "'DM Sans', sans-serif" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--amber-deep)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--gold)')}
            >
              Pedir Minha Música
            </Link>
          </div>
        </div>
      </header>

      {/* Hero strip */}
      <div className="pt-16" style={{ background: 'var(--beige-mid)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16 text-center">
          <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'var(--amber-deep)', fontFamily: "'DM Sans', sans-serif" }}>
            AUTOMAT TECNOLOGIA DA INFORMACAO LTDA · CNPJ 59.640.197/0001-76
          </p>
          <div className="flex items-center justify-center gap-3 mb-4 mt-6">
            <div className="w-10 h-10 flex items-center justify-center rounded-full" style={{ background: 'var(--gold-pale)' }}>
              <i className="ri-delete-bin-line text-lg" style={{ color: 'var(--gold)' }} />
            </div>
          </div>
          <h1
            className="text-3xl md:text-5xl mb-3"
            style={{ color: 'var(--heading-ink)', fontFamily: "'DM Sans', sans-serif", fontWeight: 800, letterSpacing: '-0.03em' }}
          >
            Exclusão de Dados
          </h1>
          <p
            className="text-sm max-w-lg mx-auto"
            style={{ color: 'var(--earth-light)', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
          >
            Inspira Músicas · Uma marca da AUTOMAT TECNOLOGIA DA INFORMACAO LTDA
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-12 md:py-16">

        {/* Intro */}
        <div className="rounded-2xl p-7 mb-8" style={{ background: 'var(--warm-white)', border: '1px solid var(--beige-mid)' }}>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--text-strong)', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
            Em conformidade com a <strong style={{ fontWeight: 600, color: 'var(--earth)' }}>Lei Geral de Proteção de Dados (LGPD)</strong> e com as políticas da Meta, você tem o direito de solicitar a exclusão de todos os dados pessoais coletados pelo serviço Inspira Músicas.
          </p>
        </div>

        {/* O que será excluído */}
        <Section icon="ri-checkbox-circle-line" title="O que será excluído">
          <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--text-strong)', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
            Ao solicitar a exclusão, removeremos:
          </p>
          <ul className="flex flex-col gap-3">
            {[
              'Seu nome e número de telefone (WhatsApp)',
              'O histórico de conversas e pedidos realizados',
              'As informações fornecidas para personalização das músicas',
              'Dados de pagamento em nosso poder',
              'Qualquer preferência ou configuração associada ao seu contato',
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--gold)' }} />
                <span className="text-sm leading-relaxed" style={{ color: 'var(--text-strong)', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </Section>

        {/* Como solicitar */}
        <Section icon="ri-send-plane-line" title="Como fazer a solicitação">
          <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--text-strong)', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
            Você pode solicitar a exclusão dos seus dados pelo canal abaixo:
          </p>

          {/* E-mail card */}
          <div
            className="rounded-xl p-5 flex items-start gap-4 transition-all duration-200"
            style={{ background: 'var(--gold-pale)', border: '1px solid var(--gold-border)' }}
          >
            <div className="w-9 h-9 flex items-center justify-center rounded-full shrink-0" style={{ background: 'var(--warm-white)' }}>
              <i className="ri-mail-line text-base" style={{ color: 'var(--gold)' }} />
            </div>
            <div>
              <p className="text-sm font-semibold mb-1" style={{ color: 'var(--earth)', fontFamily: "'DM Sans', sans-serif" }}>
                E-mail
              </p>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-strong)', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                Envie uma mensagem para{' '}
                <a
                  href="mailto:privacidade@goautomat.com"
                  className="font-medium underline underline-offset-2 cursor-pointer transition-colors duration-200"
                  style={{ color: 'var(--gold)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--amber-deep)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--gold)')}
                >
                  privacidade@goautomat.com
                </a>{' '}
                com o assunto{' '}
                <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>"Solicitação de Exclusão de Dados"</strong>{' '}
                e informe o número de telefone associado ao seu pedido.
              </p>
            </div>
          </div>
        </Section>

        {/* Prazo */}
        <Section icon="ri-time-line" title="Prazo de atendimento">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-full shrink-0 mt-0.5" style={{ background: 'var(--gold-pale)' }}>
              <i className="ri-calendar-check-line text-base" style={{ color: 'var(--gold)' }} />
            </div>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-strong)', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
              Sua solicitação será atendida em até{' '}
              <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>15 dias úteis</strong>.
              Você receberá uma confirmação assim que a exclusão for concluída.
            </p>
          </div>
        </Section>

        {/* Observações */}
        <Section icon="ri-information-line" title="Observações importantes">
          <div className="flex flex-col gap-4">
            {[
              {
                icon: 'ri-scales-line',
                text: 'Dados que precisam ser mantidos por obrigação legal (como registros fiscais) serão conservados pelo prazo mínimo exigido por lei, mesmo após a solicitação de exclusão.',
              },
              {
                icon: 'ri-alert-line',
                text: 'A exclusão é irreversível. Pedidos anteriores e músicas geradas não poderão ser recuperados após a exclusão.',
              },
              {
                icon: 'ri-bank-line',
                text: 'Se você realizou um pagamento recente, alguns dados poderão ser retidos por até 5 anos para fins de conformidade fiscal, conforme exigido pela legislação brasileira.',
              },
            ].map((obs) => (
              <div
                key={obs.icon}
                className="flex items-start gap-4 rounded-xl p-4"
                style={{ background: 'var(--warm-white)', border: '1px solid var(--beige-mid)' }}
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-full shrink-0 mt-0.5" style={{ background: 'var(--beige-mid)' }}>
                  <i className={`${obs.icon} text-sm`} style={{ color: 'var(--amber-deep)' }} />
                </div>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-strong)', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                  {obs.text}
                </p>
              </div>
            ))}
          </div>
        </Section>

        {/* Link para política */}
        <div
          className="rounded-2xl p-6 flex items-center gap-4 mt-2"
          style={{ background: 'var(--gold-pale)', border: '1px solid var(--gold-border)' }}
        >
          <div className="w-9 h-9 flex items-center justify-center rounded-full shrink-0" style={{ background: 'var(--warm-white)' }}>
            <i className="ri-shield-check-line text-base" style={{ color: 'var(--gold)' }} />
          </div>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--text-strong)', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
            Para dúvidas sobre privacidade, consulte nossa{' '}
            <Link
              to="/politica-de-privacidade"
              className="font-medium underline underline-offset-2 cursor-pointer transition-colors duration-200"
              style={{ color: 'var(--gold)' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--amber-deep)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--gold)')}
            >
              Política de Privacidade
            </Link>
            .
          </p>
        </div>

        {/* Company footer info */}
        <div
          className="mt-10 pt-8 text-center"
          style={{ borderTop: '1px solid var(--beige-mid)' }}
        >
          <p className="text-xs" style={{ color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif" }}>
            AUTOMAT TECNOLOGIA DA INFORMACAO LTDA
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif" }}>
            Rua Pais Leme, 215, Conj. 1713, Sala 01, Andar 16, Pinheiros, São Paulo/SP — CEP 05424-150
          </p>
        </div>

        {/* Back to top */}
        <div className="mt-8 text-center">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
            style={{ background: 'var(--beige-mid)', color: 'var(--earth)', fontFamily: "'DM Sans', sans-serif" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--beige-footer)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--beige-mid)')}
          >
            <i className="ri-arrow-up-line text-sm" />
            Voltar ao topo
          </button>
        </div>
      </div>

      <Footer />
    </div>
    </>
  );
}

/* ---------- helpers ---------- */
function Section({ icon, title, children }: { icon: string; title: string; children: React.ReactNode }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: 'var(--gold-pale)' }}>
          <i className={`${icon} text-sm`} style={{ color: 'var(--gold)' }} />
        </div>
        <h2
          className="text-xl md:text-2xl"
          style={{ color: 'var(--heading-ink)', fontFamily: "'DM Sans', sans-serif", fontWeight: 800, letterSpacing: '-0.03em' }}
        >
          {title}
        </h2>
      </div>
      {children}
    </div>
  );
}
